import { defineComponent, h } from 'vue';
import type { DefineComponent, PropType } from 'vue';

import type { FileDiffOptions } from '../components/FileDiff';
import { DIFFS_TAG_NAME } from '../constants';
import type {
  DiffLineAnnotation,
  FileDiffMetadata,
  SelectedLineRange,
  VirtualFileMetrics,
} from '../types';
import { useFileDiffInstance } from './composables/useFileDiffInstance';
import type { FileDiffPropsVue } from './types';
import { renderDiffChildren } from './utils/renderDiffChildren';
import { templateRender } from './utils/templateRender';

// Renders a pre-parsed `FileDiffMetadata` as a diff. Custom UI is provided via
// the `#header`, `#headerPrefix`, `#headerMetadata`, `#annotation`, and
// `#gutter` scoped slots (see `DiffSlotsVue`). `class`/`style`/etc. fall through
// to the `<diffs-container>` root via Vue attribute inheritance.
const FileDiffImpl = defineComponent({
  name: 'PierreFileDiff',
  props: {
    fileDiff: { type: Object as PropType<FileDiffMetadata>, required: true },
    options: {
      type: Object as PropType<FileDiffOptions<undefined>>,
      default: undefined,
    },
    metrics: {
      type: Object as PropType<VirtualFileMetrics>,
      default: undefined,
    },
    lineAnnotations: {
      type: Array as PropType<DiffLineAnnotation[]>,
      default: undefined,
    },
    selectedLines: {
      type: Object as PropType<SelectedLineRange | null>,
      default: undefined,
    },
    prerenderedHTML: { type: String, default: undefined },
    disableWorkerPool: { type: Boolean, default: false },
  },
  setup(props, { slots }) {
    const { containerRef, getHoveredLine } = useFileDiffInstance({
      props,
      hasCustomHeader: () => slots.header != null,
      hasGutterRenderer: () => slots.gutter != null,
    });
    return () =>
      h(
        DIFFS_TAG_NAME,
        { ref: containerRef },
        templateRender(
          renderDiffChildren({
            fileDiff: props.fileDiff,
            slots,
            lineAnnotations: props.lineAnnotations,
            getHoveredLine,
          }),
          props.prerenderedHTML
        )
      );
  },
});

// The runtime value is a valid Vue component; this explicit annotation is
// required by `isolatedDeclarations` (a bare `defineComponent(...)` initializer
// can't be emitted to a `.d.ts` without inference) and restores prop typing for
// consumers. Scoped-slot typing is documented by `DiffSlotsVue`.
export const FileDiff =
  FileDiffImpl as unknown as DefineComponent<FileDiffPropsVue>;
